import { useEffect, useState } from 'react';
import { Modal, StyleSheet, View } from 'react-native';
import { Button } from '../../../components/primitives/Button';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Heading4 } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useDeleteVoiceAgent } from '../hooks';
import type { VoiceAgent } from '../types';

interface DeleteAgentDialogProps {
  agent: VoiceAgent | null;
  organizationId: string;
  onClose: () => void;
  onDeleted?: () => void;
}

export function DeleteAgentDialog({
  agent,
  organizationId,
  onClose,
  onDeleted,
}: Readonly<DeleteAgentDialogProps>) {
  const { t } = useTranslation();
  const del = useDeleteVoiceAgent();
  const [step, setStep] = useState<1 | 2>(1);
  const visible = !!agent;

  // Reset to the first confirmation step and clear any prior error each time
  // the dialog is opened for a (possibly different) agent.
  useEffect(() => {
    if (visible) {
      setStep(1);
      del.reset();
    }
  }, [visible]);

  function handleConfirm() {
    if (!agent) return;
    del.mutate(
      { id: agent.id, organizationId },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        },
      },
    );
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card} accessibilityLabel={t('voiceAgents.deleteDialog.title')}>
          <Heading4 style={styles.title} accessibilityRole="header">
            {t('voiceAgents.deleteDialog.title')}
          </Heading4>

          <VStack spacing="sm" style={{ marginTop: tokens.spacing.md }}>
            <Body style={styles.body}>
              {step === 1
                ? `${t('voiceAgents.deleteDialog.step1Body')} "${agent?.name ?? ''}"`
                : t('voiceAgents.deleteDialog.step2Body')}
            </Body>
          </VStack>

          {del.isError ? (
            <Body
              style={styles.error}
              accessibilityLiveRegion="assertive"
              accessibilityRole="alert"
            >
              {del.error?.message ?? t('voiceAgents.deleteDialog.errorPrefix')}
            </Body>
          ) : null}

          <HStack spacing="md" style={styles.footer}>
            {step === 1 ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={onClose}
                  accessibilityLabel={t('voiceAgents.deleteDialog.cancel')}
                >
                  {t('voiceAgents.deleteDialog.cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onPress={() => setStep(2)}
                  style={styles.dangerBtn}
                  accessibilityLabel={t('voiceAgents.deleteDialog.continue')}
                >
                  {t('voiceAgents.deleteDialog.continue')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => setStep(1)}
                  disabled={del.isPending}
                  accessibilityLabel={t('voiceAgents.deleteDialog.back')}
                >
                  {t('voiceAgents.deleteDialog.back')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onPress={handleConfirm}
                  loading={del.isPending}
                  disabled={del.isPending}
                  style={styles.dangerBtn}
                  accessibilityLabel={t('voiceAgents.deleteDialog.delete')}
                >
                  {t('voiceAgents.deleteDialog.delete')}
                </Button>
              </>
            )}
          </HStack>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.status.error.border,
    padding: tokens.spacing.lg,
  },
  title: {
    color: colors.text.primary,
  },
  body: {
    color: colors.text.secondary,
    lineHeight: 22,
  },
  error: {
    color: colors.status.error.main,
    marginTop: tokens.spacing.sm,
    lineHeight: 20,
  },
  footer: {
    marginTop: tokens.spacing.lgPlus,
    justifyContent: 'flex-end',
  },
  dangerBtn: {
    backgroundColor: colors.status.error.main,
    borderColor: colors.status.error.main,
  },
});
